import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Badge,
  Divider,
  Modal,
  Spinner,
} from '@shopify/polaris';
import { useShopifyFetch, ShopInfo } from '../hooks/useShopifyFetch';

export default function Settings() {
  const { get, put, post } = useShopifyFetch();
  const queryClient = useQueryClient();

  // State
  const [brandId, setBrandId] = useState('');
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);

  // Fetch shop info
  const {
    data: shop,
    isLoading,
    error,
  } = useQuery<ShopInfo>({
    queryKey: ['shop'],
    queryFn: () => get('/api/shop'),
  });

  // Local state for settings form
  const [autoSync, setAutoSync] = useState(false);
  const [cookieDuration, setCookieDuration] = useState('30');
  const [attributionModel, setAttributionModel] = useState('last_click');
  // Guard: Save must not run until the form has hydrated from the real shop,
  // otherwise clicking Save immediately would overwrite saved config with the
  // component's defaults (data-destructive). Previously this used
  // `useState(() => …)` as if it were an effect — that initializer runs once on
  // mount when `shop` is still undefined, so the form never hydrated.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (shop?.settings) {
      setAutoSync(shop.settings.auto_sync_products ?? false);
      setCookieDuration((shop.settings.cookie_duration_days ?? 30).toString());
      setAttributionModel(shop.settings.attribution_model ?? 'last_click');
      setHydrated(true);
    }
  }, [shop]);

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: Record<string, any>) => put('/api/shop/settings', settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop'] });
    },
  });

  // Connect brand mutation
  const connectMutation = useMutation({
    mutationFn: (data: { brand_id: string }) => post('/api/shop/connect', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop'] });
      setConnectModalOpen(false);
      setBrandId('');
    },
  });

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: () => post('/api/shop/disconnect'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shop'] });
      setDisconnectModalOpen(false);
    },
  });

  // Handlers
  const handleSaveSettings = useCallback(() => {
    // Never save from an un-hydrated form (would clobber saved config).
    if (!hydrated) return;
    updateSettingsMutation.mutate({
      auto_sync_products: autoSync,
      cookie_duration_days: parseInt(cookieDuration),
      attribution_model: attributionModel,
    });
  }, [hydrated, autoSync, cookieDuration, attributionModel, updateSettingsMutation]);

  const handleConnect = useCallback(() => {
    if (brandId) {
      connectMutation.mutate({ brand_id: brandId });
    }
  }, [brandId, connectMutation]);

  const cookieDurationOptions = [
    { label: '7 days', value: '7' },
    { label: '14 days', value: '14' },
    { label: '30 days', value: '30' },
    { label: '60 days', value: '60' },
    { label: '90 days', value: '90' },
  ];

  const attributionModelOptions = [
    { label: 'Last Click', value: 'last_click' },
    { label: 'First Click', value: 'first_click' },
    { label: 'Linear', value: 'linear' },
  ];

  if (isLoading) {
    return (
      <Page title="Settings">
        <Layout>
          <Layout.Section>
            <Card>
              <Spinner />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Settings">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load settings. Please refresh the page.</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title="Settings" subtitle="Configure your Affilync integration">
      <Layout>
        {/* Affilync Connection */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">
                    Affilync Account
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    Connect your Affilync brand account to track affiliate conversions
                  </Text>
                </BlockStack>
                <Badge tone={shop?.is_connected ? 'success' : 'attention'}>
                  {shop?.is_connected ? 'Connected' : 'Not Connected'}
                </Badge>
              </InlineStack>

              <Divider />

              {shop?.is_connected ? (
                <BlockStack gap="300">
                  <InlineStack gap="200" blockAlign="center">
                    <Text variant="bodyMd" as="span">
                      Brand ID:
                    </Text>
                    <Text variant="bodyMd" as="span" fontWeight="semibold">
                      {shop.brand_id}
                    </Text>
                  </InlineStack>
                  <Button
                    tone="critical"
                    onClick={() => setDisconnectModalOpen(true)}
                    loading={disconnectMutation.isPending}
                  >
                    Disconnect Account
                  </Button>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Banner tone="info">
                    <p>
                      To start tracking affiliate conversions, connect your Affilync brand
                      account. You can find your Brand ID in your Affilync dashboard.
                    </p>
                  </Banner>
                  <Button
                    variant="primary"
                    onClick={() => setConnectModalOpen(true)}
                  >
                    Connect Affilync Account
                  </Button>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Product Sync Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Product Sync
              </Text>

              <FormLayout>
                <Checkbox
                  label="Auto-sync new products"
                  helpText="Automatically sync new products to the Affilync marketplace"
                  checked={autoSync}
                  onChange={setAutoSync}
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Attribution Settings */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Attribution Settings
              </Text>

              <FormLayout>
                <Select
                  label="Cookie Duration"
                  helpText="How long to remember the affiliate referral"
                  options={cookieDurationOptions}
                  value={cookieDuration}
                  onChange={setCookieDuration}
                />

                <Select
                  label="Attribution Model"
                  helpText="How to attribute conversions when multiple affiliates are involved"
                  options={attributionModelOptions}
                  value={attributionModel}
                  onChange={setAttributionModel}
                />
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Save Button */}
        <Layout.Section>
          <InlineStack align="end">
            <Button
              variant="primary"
              onClick={handleSaveSettings}
              loading={updateSettingsMutation.isPending}
              disabled={!hydrated}
            >
              Save Settings
            </Button>
          </InlineStack>
        </Layout.Section>

        {/* Webhook Status */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Webhook Status
              </Text>
              <Text variant="bodySm" tone="subdued" as="p">
                Webhooks are automatically registered when you install the app.
                They allow us to track orders and product changes in real-time.
              </Text>
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">Active</Badge>
                  <Text variant="bodyMd" as="span">
                    orders/paid - Conversion tracking
                  </Text>
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">Active</Badge>
                  <Text variant="bodyMd" as="span">
                    products/create - Product sync
                  </Text>
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">Active</Badge>
                  <Text variant="bodyMd" as="span">
                    products/update - Product sync
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Shop Info */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                Shop Information
              </Text>
              <BlockStack gap="200">
                <InlineStack gap="200">
                  <Text variant="bodyMd" tone="subdued" as="span">
                    Domain:
                  </Text>
                  <Text variant="bodyMd" as="span">
                    {shop?.shop_domain}
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text variant="bodyMd" tone="subdued" as="span">
                    Name:
                  </Text>
                  <Text variant="bodyMd" as="span">
                    {shop?.shop_name || '-'}
                  </Text>
                </InlineStack>
                <InlineStack gap="200">
                  <Text variant="bodyMd" tone="subdued" as="span">
                    Installed:
                  </Text>
                  <Text variant="bodyMd" as="span">
                    {shop?.installed_at
                      ? new Date(shop.installed_at).toLocaleDateString()
                      : '-'}
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Connect Modal */}
      <Modal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        title="Connect Affilync Account"
        primaryAction={{
          content: 'Connect',
          onAction: handleConnect,
          loading: connectMutation.isPending,
          disabled: !brandId,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setConnectModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text as="p">
              Enter your Affilync Brand ID to connect your account. You can find this
              in your Affilync dashboard under Brand Settings.
            </Text>
            <TextField
              label="Brand ID"
              value={brandId}
              onChange={setBrandId}
              placeholder="e.g., 550e8400-e29b-41d4-a716-446655440000"
              autoComplete="off"
            />
            {connectMutation.isError && (
              <Banner tone="critical">
                <p>Failed to connect. Please check your Brand ID and try again.</p>
              </Banner>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Legal & Privacy Links */}
      <Layout.Section>
        <Card>
          <BlockStack gap="300">
            <Text variant="headingMd" as="h2">
              Legal
            </Text>
            <Divider />
            <InlineStack gap="400">
              <Button
                variant="plain"
                url="https://affilync.com/privacy"
                external
              >
                Privacy Policy
              </Button>
              <Button
                variant="plain"
                url="https://affilync.com/terms"
                external
              >
                Terms of Service
              </Button>
              <Button
                variant="plain"
                url="https://affilync.com/support"
                external
              >
                Support & Help
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </Layout.Section>

      {/* Disconnect Confirmation Modal */}
      <Modal
        open={disconnectModalOpen}
        onClose={() => setDisconnectModalOpen(false)}
        title="Disconnect Affilync Account"
        primaryAction={{
          content: 'Disconnect',
          onAction: () => disconnectMutation.mutate(),
          loading: disconnectMutation.isPending,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setDisconnectModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <Banner tone="warning">
            <p>
              Are you sure you want to disconnect your Affilync account? This will:
            </p>
            <ul>
              <li>Stop tracking affiliate conversions</li>
              <li>Remove synced products from the marketplace</li>
              <li>Disable affiliate links for your products</li>
            </ul>
            <p>Your historical data will be preserved in Affilync.</p>
          </Banner>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
